import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ArrowLeft, Download, ClipboardList, User, Clock, Calendar as CalendarIcon, X } from "lucide-react";
import html2canvas from "html2canvas";
import { format } from "date-fns";
import logoImage from "@/assets/skylink-logo.png";
import Footer from "@/components/Footer";

interface Instructor {
  id: string;
  name: string;
}

interface Subject {
  id: string;
  name: string;
}

interface Batch {
  id: string;
  name: string;
}

interface Student {
  id: string;
  first_name: string;
  last_name: string;
  batch_id: string | null;
}

interface SubjectClass {
  id: string;
  class_name: string;
  instructor_id: string;
  subject_id: string;
  status: string;
  instructor?: { name: string };
  subject?: { name: string };
}

export default function FlightSlots() {
  const navigate = useNavigate();
  const downloadRef = useRef<HTMLDivElement>(null);
  
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [allStudents, setAllStudents] = useState<Student[]>([]);
  const [batchStudents, setBatchStudents] = useState<Student[]>([]);
  const [additionalStudents, setAdditionalStudents] = useState<Student[]>([]);
  const [subjectClasses, setSubjectClasses] = useState<SubjectClass[]>([]);
  const [classStudents, setClassStudents] = useState<Student[]>([]);
  
  const [selectedInstructor, setSelectedInstructor] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedBatches, setSelectedBatches] = useState<string[]>([]);
  const [timeStart, setTimeStart] = useState("");
  const [timeEnd, setTimeEnd] = useState("");
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [notes, setNotes] = useState("");
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [selectedAdditionalStudents, setSelectedAdditionalStudents] = useState<string[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [isExam, setIsExam] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bookedSlots, setBookedSlots] = useState<{time_start: string, time_end: string, slot_date: string, instructor_id: string, instructors?: {name: string}}[]>([]);
  const [overbookingError, setOverbookingError] = useState<string>("");
  const [useClassSelection, setUseClassSelection] = useState(true);

  useEffect(() => {
    const init = async () => {
      await checkAuth();
      await fetchData();
    };
    init();
  }, []);

  useEffect(() => {
    if (selectedBatches.length > 0) {
      fetchBatchStudents();
    } else {
      setBatchStudents([]);
      setSelectedStudents([]);
    }
  }, [selectedBatches]);

  useEffect(() => {
    if (selectedBatches.length > 0) {
      const filtered = allStudents.filter(s => !selectedBatches.includes(s.batch_id || ''));
      setAdditionalStudents(filtered);
    } else {
      setAdditionalStudents(allStudents);
    }
  }, [selectedBatches, allStudents]);

  useEffect(() => {
    if (selectedDates.length > 0) {
      fetchBookedSlots();
    } else {
      setBookedSlots([]);
    }
  }, [selectedDates]);

  // Fetch class students when a class is selected (admin mode)
  useEffect(() => {
    const fetchClassStudents = async () => {
      if (!selectedClass || !useClassSelection) {
        setClassStudents([]);
        return;
      }

      const { data } = await supabase
        .from("subject_class_students")
        .select("student:students(id, first_name, last_name, batch_id)")
        .eq("subject_class_id", selectedClass);

      if (data) {
        const students = data
          .map(d => d.student as any)
          .filter(Boolean);
        setClassStudents(students);
        // Auto-select all students from the class
        setSelectedStudents(students.map((s: Student) => s.id));
        setSelectedAdditionalStudents([]);
      }
    };

    fetchClassStudents();
  }, [selectedClass, useClassSelection]);

  // Auto-set instructor and subject when class is selected
  useEffect(() => {
    if (selectedClass && useClassSelection) {
      const selectedClassData = subjectClasses.find(c => c.id === selectedClass);
      if (selectedClassData) {
        setSelectedInstructor(selectedClassData.instructor_id);
        setSelectedSubject(selectedClassData.subject_id);
      }
    }
  }, [selectedClass, subjectClasses, useClassSelection]);

  const checkAuth = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate("/auth");
      return false;
    }

    const { data: roles } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user.id);

    const adminStatus = roles?.some(r => r.role === "admin" || r.role === "manager" || r.role === "super_admin") || false;
    setIsAdmin(adminStatus);
    return adminStatus;
  };

  const fetchData = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data: roles } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user.id);

    const adminStatus = roles?.some(r => r.role === "admin" || r.role === "manager" || r.role === "super_admin") || false;
    setIsAdmin(adminStatus);

    if (adminStatus) {
      // Admin: fetch all active instructors
      const { data: instructorsData } = await supabase
        .from("instructors")
        .select("id, name")
        .eq("status", "Active");
      if (instructorsData && instructorsData.length > 0) {
        setInstructors(instructorsData);
      }
      
      // Admin: fetch all subjects
      const { data: subjectsData } = await supabase.from("subjects").select("id, name");
      if (subjectsData) setSubjects(subjectsData);

      // Admin: fetch all active subject classes
      const { data: classesData } = await supabase
        .from("subject_classes")
        .select("id, class_name, instructor_id, subject_id, status, instructors:instructor_id(name), subjects:subject_id(name)")
        .eq("status", "Active");
      if (classesData) {
        setSubjectClasses(classesData.map(c => ({
          id: c.id,
          class_name: c.class_name,
          instructor_id: c.instructor_id,
          subject_id: c.subject_id,
          status: c.status,
          instructor: c.instructors as any,
          subject: c.subjects as any,
        })));
      }
    } else {
      // Instructor: auto-select themselves
      const { data: currentInstructor } = await supabase
        .from("instructors")
        .select("id, name")
        .eq("user_id", user.id)
        .maybeSingle();
      
      if (currentInstructor) {
        setInstructors([currentInstructor]);
        setSelectedInstructor(currentInstructor.id);
        
        // Instructor: only fetch subjects from their active classes
        const { data: activeClasses } = await supabase
          .from("subject_classes")
          .select("subject_id, subject:subjects(id, name)")
          .eq("instructor_id", currentInstructor.id)
          .eq("status", "Active");
        
        if (activeClasses && activeClasses.length > 0) {
          const uniqueSubjects = new Map<string, Subject>();
          activeClasses.forEach(c => {
            const subj = c.subject as any;
            if (subj && !uniqueSubjects.has(subj.id)) {
              uniqueSubjects.set(subj.id, { id: subj.id, name: subj.name });
            }
          });
          setSubjects(Array.from(uniqueSubjects.values()));
        } else {
          setSubjects([]);
          toast.info("You have no active classes. Create a class first to request slots.");
        }
      } else {
        toast.error("Instructor profile not found");
        navigate("/dashboard");
        return;
      }
    }

    // Fetch batches and students
    const [batchesRes, studentsRes] = await Promise.all([
      supabase.from("batches").select("id, name"),
      supabase.from("students").select("id, first_name, last_name, batch_id"),
    ]);

    if (batchesRes.data) setBatches(batchesRes.data);
    if (studentsRes.data) setAllStudents(studentsRes.data);
  };

  const fetchBatchStudents = async () => {
    if (selectedBatches.length === 0) return;
    
    const { data } = await supabase
      .from("students")
      .select("id, first_name, last_name, batch_id")
      .in("batch_id", selectedBatches);

    if (data) setBatchStudents(data);
  };

  const fetchBookedSlots = async () => {
    if (selectedDates.length === 0) return;

    const dateStrings = selectedDates.map(date => format(date, "yyyy-MM-dd"));
    // Fetch ALL slots on selected dates to check for any conflicts
    const { data } = await supabase
      .from("flight_slots")
      .select("time_start, time_end, slot_date, instructor_id, instructors(name)")
      .in("slot_date", dateStrings);

    if (data) setBookedSlots(data as any);
  };

  const handleStudentToggle = (studentId: string, isFromBatch: boolean) => {
    if (isFromBatch) {
      setSelectedStudents(prev =>
        prev.includes(studentId)
          ? prev.filter(id => id !== studentId)
          : [...prev, studentId]
      );
    } else {
      setSelectedAdditionalStudents(prev =>
        prev.includes(studentId)
          ? prev.filter(id => id !== studentId)
          : [...prev, studentId]
      );
    }
  };

  const handleSubmit = async () => {
    if (!selectedInstructor || !selectedSubject || !timeStart || !timeEnd) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (selectedDates.length === 0) {
      toast.error("Please select at least one date");
      return;
    }

    const allSelectedStudents = [...selectedStudents, ...selectedAdditionalStudents];
    console.log("Selected students:", allSelectedStudents);
    
    if (allSelectedStudents.length === 0) {
      toast.error("Please select at least one student from the list below");
      return;
    }

    setLoading(true);

    // Check for conflicts on each selected date - ANY instructor (admins included)
    if (!isExam) {
      const dateStrings = selectedDates.map(date => format(date, "yyyy-MM-dd"));
      const { data: existingSlots } = await supabase
        .from("flight_slots")
        .select("id, time_start, time_end, slot_date, instructor_id, instructors(name)")
        .in("slot_date", dateStrings);

      if (existingSlots && existingSlots.length > 0) {
        const conflictingDates: string[] = [];
        
        dateStrings.forEach(dateStr => {
          const slotsOnDate = existingSlots.filter(slot => slot.slot_date === dateStr);
          const hasConflict = slotsOnDate.some(slot => {
            // Check for time overlap with ANY instructor
            return (timeStart < slot.time_end && timeEnd > slot.time_start);
          });
          
          if (hasConflict) {
            const conflictingSlot = slotsOnDate.find(slot => 
              timeStart < slot.time_end && timeEnd > slot.time_start
            );
            const instructorName = (conflictingSlot?.instructors as any)?.name || "Unknown";
            conflictingDates.push(`${format(new Date(dateStr), "MMM dd, yyyy")} (${instructorName}: ${conflictingSlot?.time_start}-${conflictingSlot?.time_end})`);
          }
        });

        if (conflictingDates.length > 0) {
          setOverbookingError(`Time slot already booked on: ${conflictingDates.join(", ")}. Please choose different times or enable exam mode.`);
          setLoading(false);
          return;
        }
      }

      // Check for student overbooking - if any selected student is already in another slot at the same time
      const { data: existingSlotStudents } = await supabase
        .from("flight_slot_students")
        .select("student_id, flight_slots(time_start, time_end, slot_date, instructors(name))")
        .in("student_id", allSelectedStudents);

      if (existingSlotStudents && existingSlotStudents.length > 0) {
        const studentConflicts: string[] = [];
        
        allSelectedStudents.forEach(studentId => {
          const studentSlots = existingSlotStudents.filter(ss => ss.student_id === studentId);
          
          dateStrings.forEach(dateStr => {
            const conflictingSlot = studentSlots.find(ss => {
              const slot = ss.flight_slots as any;
              if (!slot || slot.slot_date !== dateStr) return false;
              // Check for time overlap
              return (timeStart < slot.time_end && timeEnd > slot.time_start);
            });
            
            if (conflictingSlot) {
              const student = allStudents.find(s => s.id === studentId);
              const slot = conflictingSlot.flight_slots as any;
              const studentName = `${student?.first_name} ${student?.last_name || ""}`.trim();
              const instructorName = slot?.instructors?.name || "Unknown";
              studentConflicts.push(`${studentName} is already booked on ${format(new Date(dateStr), "MMM dd, yyyy")} at ${slot?.time_start}-${slot?.time_end} with ${instructorName}`);
            }
          });
        });

        if (studentConflicts.length > 0) {
          setOverbookingError(`Student overbooking detected:\n\n${studentConflicts.join("\n\n")}.\n\nPlease remove these students or choose different times.`);
          setLoading(false);
          return;
        }
      }
    }

    const { data: { user } } = await supabase.auth.getUser();

    // Create slots for each selected date
    // Instructors create pending requests, admins create approved slots
    const slotInserts = selectedDates.map(date => ({
      instructor_id: selectedInstructor,
      subject_id: selectedSubject,
      batch_id: selectedBatches.length === 1 ? selectedBatches[0] : null,
      time_start: timeStart,
      time_end: timeEnd,
      slot_date: format(date, "yyyy-MM-dd"),
      notes: isExam ? `EXAM - ${notes}` : notes,
      created_by: user?.id,
      created_by_admin: isAdmin,
      status: isAdmin ? "approved" : "pending",
    }));

    const { data: createdSlots, error: slotError } = await supabase
      .from("flight_slots")
      .insert(slotInserts)
      .select();

    if (slotError || !createdSlots) {
      console.error("Slot creation error:", slotError);
      if (slotError?.code === "23505") {
        toast.error("Slot already exists with this exact configuration");
      } else {
        toast.error(`Failed to create slots: ${slotError?.message || "Unknown error"}`);
      }
      setLoading(false);
      return;
    }

    // Add students to each created slot
    const studentInserts = createdSlots.flatMap(slot =>
      allSelectedStudents.map(studentId => ({
        slot_id: slot.id,
        student_id: studentId,
      }))
    );

    const { error: studentsError } = await supabase
      .from("flight_slot_students")
      .insert(studentInserts);

    if (studentsError) {
      console.error("Student assignment error:", studentsError);
      toast.error(`Failed to add students to slots: ${studentsError.message}`);
      setLoading(false);
      return;
    }

    // If instructor submitted a request, notify all admins/managers
    if (!isAdmin) {
      const instructorName = instructors.find(i => i.id === selectedInstructor)?.name || "An instructor";
      const subjectName = subjects.find(s => s.id === selectedSubject)?.name || "a subject";
      
      // Get all admin/manager user IDs
      const { data: adminRoles } = await supabase
        .from("user_roles")
        .select("user_id")
        .in("role", ["admin", "manager", "super_admin"]);

      if (adminRoles && adminRoles.length > 0) {
        const notifications = adminRoles.map(role => ({
          user_id: role.user_id,
          title: "New Slot Request",
          message: `${instructorName} has requested ${createdSlots.length} slot(s) for ${subjectName} on ${selectedDates.map(d => format(d, "MMM dd")).join(", ")}`,
          type: "slot_request",
          priority: "high",
          link: "/view-slots",
        }));

        await supabase.from("notifications").insert(notifications);
      }
    }

    setLoading(false);
    
    if (isAdmin) {
      toast.success(`${createdSlots.length} slot(s) booked successfully!`);
      // Show preview after successful booking
      setTimeout(() => {
        setShowPreview(true);
      }, 500);
    } else {
      toast.success(`${createdSlots.length} slot request(s) submitted for approval!`);
      navigate("/view-slots");
    }
  };

  const handleDownload = async () => {
    if (!downloadRef.current) return;

    try {
      const canvas = await html2canvas(downloadRef.current, {
        backgroundColor: "#ffffff",
        scale: 2,
      });

      const link = document.createElement("a");
      link.download = `flight-slot-${new Date().toISOString().split("T")[0]}.png`;
      link.href = canvas.toDataURL();
      link.click();

      toast.success("Slot schedule downloaded!");
    } catch (error) {
      toast.error("Failed to download schedule");
    }
  };

  const getInstructorName = () => instructors.find(i => i.id === selectedInstructor)?.name || "";
  const getSubjectName = () => subjects.find(s => s.id === selectedSubject)?.name || "";
  const getBatchNames = () => {
    if (isAdmin && useClassSelection && selectedClass) {
      const cls = subjectClasses.find(c => c.id === selectedClass);
      return cls?.class_name || "Class";
    }
    if (selectedBatches.length === 0) return "No Batch";
    return selectedBatches.map(id => batches.find(b => b.id === id)?.name).filter(Boolean).join(', ');
  };
  const getSelectedStudentsList = () => {
    const allIds = [...selectedStudents, ...selectedAdditionalStudents];
    // Include class students in the list
    const allAvailable = [...allStudents, ...classStudents];
    const uniqueStudents = new Map();
    allAvailable.forEach(s => uniqueStudents.set(s.id, s));
    return Array.from(uniqueStudents.values())
      .filter(s => allIds.includes(s.id))
      .map(s => `${s.first_name} ${s.last_name || ""}`);
  };

  if (showPreview) {
    return (
      <div className="container mx-auto p-6 max-w-4xl">
        <div className="flex gap-4 mb-6">
          <Button onClick={() => setShowPreview(false)} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button onClick={handleDownload} className="ml-auto">
            <Download className="w-4 h-4 mr-2" />
            Download as Image
          </Button>
        </div>

        <div ref={downloadRef} className="bg-white p-8 rounded-lg shadow-lg">
          <div className="flex items-center justify-between mb-6">
            <img src={logoImage} alt="Skylink Aviation" className="h-16" />
            <div className="text-right">
              <div className="flex items-center gap-2 justify-end">
                <ClipboardList className="w-6 h-6 text-primary" />
                <h1 className="text-2xl font-bold text-primary">Slot Schedule</h1>
              </div>
              <p className="text-sm text-muted-foreground">Skylink Aviation</p>
            </div>
          </div>

          <div className="border-t-2 border-primary pt-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-start gap-2">
                <User className="w-5 h-5 text-primary mt-1" />
                <div>
                  <p className="text-sm font-semibold text-muted-foreground">Instructor</p>
                  <p className="text-lg font-medium">{getInstructorName()}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <ClipboardList className="w-5 h-5 text-primary mt-1" />
                <div>
                  <p className="text-sm font-semibold text-muted-foreground">Subject</p>
                  <p className="text-lg font-medium">{getSubjectName()}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <User className="w-5 h-5 text-primary mt-1" />
                <div>
                  <p className="text-sm font-semibold text-muted-foreground">Batch</p>
                  <p className="text-lg font-medium">{getBatchNames()}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Clock className="w-5 h-5 text-primary mt-1" />
                <div>
                  <p className="text-sm font-semibold text-muted-foreground">Time Slot</p>
                  <p className="text-lg font-medium">{timeStart} - {timeEnd}</p>
                </div>
              </div>
              {selectedDates.length > 0 && (
                <div className="flex items-start gap-2">
                  <CalendarIcon className="w-5 h-5 text-primary mt-1" />
                  <div>
                    <p className="text-sm font-semibold text-muted-foreground">Dates</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selectedDates.map((date, idx) => (
                        <span key={idx} className="text-sm font-medium bg-muted px-2 py-1 rounded">
                          {format(date, "MMM dd, yyyy")}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6">
              <div className="flex items-center gap-2 mb-3">
                <User className="w-5 h-5 text-primary" />
                <p className="text-sm font-semibold text-muted-foreground">Students</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {getSelectedStudentsList().map((name, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-2 bg-muted rounded">
                    <User className="w-4 h-4 text-primary" />
                    <span className="text-sm">{name}</span>
                  </div>
                ))}
              </div>
            </div>

            {notes && (
              <div className="mt-6">
                <p className="text-sm font-semibold text-muted-foreground mb-2">Notes</p>
                <p className="text-sm">{notes}</p>
              </div>
            )}
          </div>

          <div className="mt-8 pt-4 border-t text-center text-xs text-muted-foreground">
            Generated on {new Date().toLocaleString()}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 container mx-auto p-3 sm:p-6 max-w-4xl">
        <Button onClick={() => navigate("/dashboard")} variant="outline" className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
            <ClipboardList className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
            {isAdmin ? "Slot Booking" : "Request Slot"}
          </CardTitle>
          {!isAdmin && (
            <p className="text-sm text-muted-foreground">
              Your request will be sent to admin for approval
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-4 sm:space-y-6">
          {loading && (
            <div className="flex items-center justify-center gap-2 p-4 bg-muted rounded-lg">
              <ClipboardList className="w-5 h-5 text-primary animate-pulse" />
              <span className="text-sm">{isAdmin ? "Booking slot..." : "Submitting request..."}</span>
            </div>
          )}

          {/* Admin: Toggle between class selection and manual selection */}
          {isAdmin && (
            <div className="flex items-center space-x-2 p-4 bg-primary/5 rounded-lg border border-primary/20">
              <Checkbox
                id="use-class-selection"
                checked={useClassSelection}
                onCheckedChange={(checked) => {
                  setUseClassSelection(checked as boolean);
                  // Reset selections when toggling
                  setSelectedClass("");
                  setSelectedInstructor("");
                  setSelectedSubject("");
                  setSelectedStudents([]);
                  setSelectedAdditionalStudents([]);
                  setClassStudents([]);
                  setBatchStudents([]);
                  setSelectedBatches([]);
                }}
              />
              <label
                htmlFor="use-class-selection"
                className="text-sm font-medium leading-none cursor-pointer"
              >
                Quick book by selecting a class (auto-selects all students)
              </label>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4">
            {/* Admin class selection mode */}
            {isAdmin && useClassSelection && (
              <div>
                <Label>Select Class *</Label>
                <Select value={selectedClass} onValueChange={setSelectedClass}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select an active class" />
                  </SelectTrigger>
                  <SelectContent>
                    {subjectClasses.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.class_name} ({c.instructor?.name} - {c.subject?.name})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Selecting a class will auto-select the instructor, subject, and all enrolled students
                </p>
              </div>
            )}

            {/* Show instructor/subject only if not using class selection or for instructors */}
            {(!isAdmin || !useClassSelection) && (
              <>
                <div>
                  <Label>Instructor *</Label>
                  <Select 
                    value={selectedInstructor} 
                    onValueChange={setSelectedInstructor}
                    disabled={!isAdmin}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select instructor" />
                    </SelectTrigger>
                    <SelectContent>
                      {instructors.map(i => (
                        <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!isAdmin && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Your instructor account is automatically selected
                    </p>
                  )}
                </div>

                <div>
                  <Label>Subject *</Label>
                  <Select value={selectedSubject} onValueChange={setSelectedSubject}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select subject" />
                    </SelectTrigger>
                    <SelectContent>
                      {subjects.map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {/* Show auto-selected details when using class selection */}
            {isAdmin && useClassSelection && selectedClass && (
              <div className="p-3 bg-muted rounded-lg space-y-2">
                <p className="text-sm">
                  <span className="font-medium">Instructor:</span>{" "}
                  {instructors.find(i => i.id === selectedInstructor)?.name || "Not set"}
                </p>
                <p className="text-sm">
                  <span className="font-medium">Subject:</span>{" "}
                  {subjects.find(s => s.id === selectedSubject)?.name || "Not set"}
                </p>
                <p className="text-sm">
                  <span className="font-medium">Students:</span>{" "}
                  {classStudents.length} students selected
                </p>
              </div>
            )}

            {/* Batch selection removed - now using checkbox list above */}

            <div>
              <Label>Select Dates *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {selectedDates.length > 0
                      ? `${selectedDates.length} date(s) selected`
                      : "Pick dates"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="multiple"
                    selected={selectedDates}
                    onSelect={(dates) => setSelectedDates(dates || [])}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
              {selectedDates.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {selectedDates.map((date, idx) => (
                    <Badge key={idx} variant="secondary" className="gap-1">
                      {format(date, "MMM dd, yyyy")}
                      <button
                        type="button"
                        onClick={() => setSelectedDates(selectedDates.filter((_, i) => i !== idx))}
                        className="ml-1 hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div>
              <Label>Start Time *</Label>
              <Input
                type="time"
                value={timeStart}
                onChange={(e) => setTimeStart(e.target.value)}
              />
            </div>

            <div>
              <Label>End Time *</Label>
              <Input
                type="time"
                value={timeEnd}
                onChange={(e) => setTimeEnd(e.target.value)}
              />
            </div>
          </div>

          <div>
            <Label>Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional information..."
              rows={3}
            />
          </div>

          {bookedSlots.length > 0 && selectedDates.length > 0 && (
            <div className="p-4 bg-accent/10 rounded-lg border border-accent/20">
              <p className="text-sm font-semibold mb-2 flex items-center gap-2">
                <Clock className="w-4 h-4 text-primary" />
                Already Booked Time Slots on Selected Dates:
              </p>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {selectedDates.map((date, dateIdx) => {
                  const dateStr = format(date, "yyyy-MM-dd");
                  const slotsOnDate = bookedSlots.filter(slot => slot.slot_date === dateStr);
                  
                  if (slotsOnDate.length === 0) return null;
                  
                  return (
                    <div key={dateIdx} className="border-l-2 border-primary pl-2">
                      <p className="text-xs font-medium text-muted-foreground mb-1">
                        {format(date, "MMM dd, yyyy")}
                      </p>
                      {slotsOnDate.map((slot, slotIdx) => (
                        <div key={slotIdx} className="text-sm text-muted-foreground">
                          • {slot.time_start} - {slot.time_end} ({slot.instructors?.name || "Unknown"})
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                ⚠️ You cannot book overlapping time slots. Choose times after existing bookings or enable exam mode.
              </p>
            </div>
          )}

          <div className="flex items-center space-x-2 p-4 bg-accent/10 rounded-lg border border-accent/20">
            <Checkbox
              id="exam-mode"
              checked={isExam}
              onCheckedChange={(checked) => setIsExam(checked as boolean)}
            />
            <label
              htmlFor="exam-mode"
              className="text-sm font-medium leading-none cursor-pointer"
            >
              This is an exam slot (allows duplicate time bookings)
            </label>
          </div>

          {/* Show class students when using class selection */}
          {isAdmin && useClassSelection && classStudents.length > 0 && (
            <div>
              <Label className="mb-3 block">Class Students (all selected)</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto border rounded p-3 bg-muted/30">
                {classStudents.map(student => (
                  <div key={student.id} className="flex items-center space-x-2 p-1">
                    <Checkbox
                      id={`class-${student.id}`}
                      checked={selectedStudents.includes(student.id)}
                      onCheckedChange={() => handleStudentToggle(student.id, true)}
                    />
                    <label htmlFor={`class-${student.id}`} className="text-sm cursor-pointer flex-1 break-words">
                      {student.first_name} {student.last_name || ""}
                    </label>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                You can uncheck students if needed
              </p>
            </div>
          )}

          {/* Show batch students only if NOT using class selection */}
          {(!isAdmin || !useClassSelection) && batchStudents.length > 0 && (
            <div>
              <Label className="mb-3 block">Students from Batch</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto border rounded p-3">
                {batchStudents.map(student => (
                  <div key={student.id} className="flex items-center space-x-2 p-1">
                    <Checkbox
                      id={`batch-${student.id}`}
                      checked={selectedStudents.includes(student.id)}
                      onCheckedChange={() => handleStudentToggle(student.id, true)}
                    />
                    <label htmlFor={`batch-${student.id}`} className="text-sm cursor-pointer flex-1 break-words">
                      {student.first_name} {student.last_name || ""}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(!isAdmin || !useClassSelection) && additionalStudents.length > 0 && (
            <div>
              <Label className="mb-3 block">Additional Students</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto border rounded p-3">
                {additionalStudents.map(student => (
                  <div key={student.id} className="flex items-center space-x-2 p-1">
                    <Checkbox
                      id={`add-${student.id}`}
                      checked={selectedAdditionalStudents.includes(student.id)}
                      onCheckedChange={() => handleStudentToggle(student.id, false)}
                    />
                    <label htmlFor={`add-${student.id}`} className="text-sm cursor-pointer flex-1 break-words">
                      {student.first_name} {student.last_name || ""}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          <Button onClick={handleSubmit} className="w-full" disabled={loading}>
            {loading ? (
              <>
                <ClipboardList className="w-4 h-4 mr-2 animate-pulse" />
                {isAdmin ? "Booking Slot..." : "Submitting Request..."}
              </>
            ) : (
              <>
                <ClipboardList className="w-4 h-4 mr-2" />
                {isAdmin ? "Book Slot" : "Request Slot"}
              </>
            )}
          </Button>
        </CardContent>
      </Card>
      </div>
      <Footer />

      {/* Overbooking Error Dialog */}
      <AlertDialog open={!!overbookingError} onOpenChange={() => setOverbookingError("")}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Booking Conflict</AlertDialogTitle>
            <AlertDialogDescription className="whitespace-pre-line">
              {overbookingError}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setOverbookingError("")}>
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
